import Editor, { loader } from "@monaco-editor/react";
import type { ThemeType } from "@repo/shared/types/theme";
import type * as Monaco from "monaco-editor";
import { useEffect, useMemo, useRef, useState } from "react";

import { computeCharLevelRanges, computeSqlDiff } from "../lib/sql-diff";
import { setupSqlMonaco, THEME_COLORS } from "../lib/sql-monaco";
import { Skeleton } from "../ui/skeleton";

let warmed = false;

/**
 * Kicks off Monaco's background load so the SQL editor is ready before the
 * user reaches a page that mounts it. `@monaco-editor/react` exposes a
 * singleton `loader`, so `loader.init()` warms the exact instance the
 * `<Editor>` consumes. Idempotent and safe to call repeatedly.
 */
export const preloadSqlEditor = () => {
  if (warmed) return;
  warmed = true;
  loader.init().catch(() => {
    // Allow a later retry if the initial (CDN) fetch failed.
    warmed = false;
  });
};

type SQLEditorProps = {
  value: string;
  onChange: (
    value: string | undefined,
    ev: Monaco.editor.IModelContentChangedEvent,
  ) => void;
  theme: ThemeType;
  onCommandEnter?: () => void;
  schemas?: Schema;
  placeholder?: string;
  /**
   * When set and different from `value`, renders a read-only inline diff of
   * `baselineQuery` (original, red) vs `value` (current, green) so the user
   * can see their unsaved changes. No accept/reject controls.
   */
  baselineQuery?: string;
};

type Table = {
  name: string;
  columns: {
    name: string;
    type: string;
  }[];
};

type Schema = {
  name: string;
  tables: Table[];
}[];

const SQL_KEYWORDS = [
  "SELECT",
  "FROM",
  "WHERE",
  "AND",
  "OR",
  "NOT",
  "IN",
  "LIKE",
  "BETWEEN",
  "IS NULL",
  "IS NOT NULL",
  "ORDER BY",
  "GROUP BY",
  "HAVING",
  "LIMIT",
  "OFFSET",
  "JOIN",
  "LEFT JOIN",
  "RIGHT JOIN",
  "INNER JOIN",
  "OUTER JOIN",
  "FULL JOIN",
  "CROSS JOIN",
  "ON",
  "AS",
  "DISTINCT",
  "COUNT",
  "SUM",
  "AVG",
  "MIN",
  "MAX",
  "CASE",
  "WHEN",
  "THEN",
  "ELSE",
  "END",
  "INSERT INTO",
  "VALUES",
  "UPDATE",
  "SET",
  "DELETE",
  "CREATE TABLE",
  "ALTER TABLE",
  "DROP TABLE",
  "UNION",
  "UNION ALL",
  "EXCEPT",
  "INTERSECT",
  "WITH",
  "ASC",
  "DESC",
  "NULLS FIRST",
  "NULLS LAST",
];

const LINE_HEIGHT = 18;
const FONT_SIZE = 13;

export const SQLEditor = ({
  value,
  onChange,
  theme,
  onCommandEnter,
  schemas,
  placeholder,
  baselineQuery,
}: SQLEditorProps) => {
  // Use a ref to always have access to the latest callback
  // Monaco's addCommand captures the callback at mount time, so we need this pattern
  const onCommandEnterRef = useRef(onCommandEnter);
  onCommandEnterRef.current = onCommandEnter;

  // Store monaco instance and editor for use in useEffect
  const [monaco, setMonaco] = useState<typeof Monaco | null>(null);
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const completionDisposableRef = useRef<Monaco.IDisposable | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [contentLeft, setContentLeft] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const decorationsCollectionRef =
    useRef<Monaco.editor.IEditorDecorationsCollection | null>(null);
  const viewZoneIdsRef = useRef<string[]>([]);

  const hunks = useMemo(
    () => (baselineQuery ? computeSqlDiff(baselineQuery, value) : []),
    [baselineQuery, value],
  );

  const editorValue = value;

  const handleChange = (
    newVal: string | undefined,
    ev: Monaco.editor.IModelContentChangedEvent,
  ) => {
    onChange(newVal, ev);
  };

  const handleBeforeMount = (monacoInstance: typeof Monaco) => {
    setupSqlMonaco(monacoInstance);
    setMonaco(monacoInstance);
  };

  // Register/update completion provider when schemas change or monaco becomes available
  useEffect(() => {
    if (!monaco) return;

    // Dispose previous provider if exists
    completionDisposableRef.current?.dispose();

    if (!schemas || schemas.length === 0) return;

    // Register SQL completion provider
    completionDisposableRef.current =
      monaco.languages.registerCompletionItemProvider("sql", {
        triggerCharacters: [" ", ".", "'", '"', "`"],
        provideCompletionItems: (model, position) => {
          const textUntilPosition = model.getValueInRange({
            startLineNumber: 1,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });

          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };

          const suggestions: Monaco.languages.CompletionItem[] = [];

          // Flatten all tables from all schemas for easy lookup
          const allTables = schemas.flatMap((s) =>
            s.tables.map((t) => ({ ...t, schema: s.name })),
          );

          // Check if we're after a dot (schema.table or table.column context)
          const beforeCursor = textUntilPosition.slice(-100);
          const doubleDotMatch = beforeCursor.match(/(\w+)\.(\w+)\.\s*\w*$/);
          const singleDotMatch = beforeCursor.match(/(\w+)\.\s*\w*$/);

          if (doubleDotMatch) {
            // schema.table.column context - suggest columns
            const schemaName = doubleDotMatch[1]?.toLowerCase();
            const tableName = doubleDotMatch[2]?.toLowerCase();
            const schemaObj = schemas.find(
              (s) => s.name.toLowerCase() === schemaName,
            );
            const table = schemaObj?.tables.find(
              (t) => t.name.toLowerCase() === tableName,
            );
            if (table) {
              for (const column of table.columns) {
                suggestions.push({
                  label: column.name,
                  kind: monaco.languages.CompletionItemKind.Field,
                  detail: column.type,
                  insertText: column.name,
                  range,
                });
              }
            }
          } else if (singleDotMatch) {
            const name = singleDotMatch[1]?.toLowerCase();
            if (!name) return { suggestions };
            // Check if it's a schema name - suggest tables
            const schemaObj = schemas.find(
              (s) => s.name.toLowerCase() === name,
            );
            if (schemaObj) {
              for (const table of schemaObj.tables) {
                suggestions.push({
                  label: table.name,
                  kind: monaco.languages.CompletionItemKind.Class,
                  detail: `Table (${schemaObj.name})`,
                  insertText: table.name,
                  range,
                });
              }
            } else {
              // Check if it's a table name - suggest columns
              const table = allTables.find(
                (t) => t.name.toLowerCase() === name,
              );
              if (table) {
                for (const column of table.columns) {
                  suggestions.push({
                    label: column.name,
                    kind: monaco.languages.CompletionItemKind.Field,
                    detail: column.type,
                    insertText: column.name,
                    range,
                  });
                }
              }
            }
          } else {
            // Add SQL keywords
            for (const keyword of SQL_KEYWORDS) {
              suggestions.push({
                label: keyword,
                kind: monaco.languages.CompletionItemKind.Keyword,
                insertText: keyword,
                range,
              });
            }

            // Add schema names
            for (const schemaObj of schemas) {
              suggestions.push({
                label: schemaObj.name,
                kind: monaco.languages.CompletionItemKind.Module,
                detail: "Schema",
                insertText: schemaObj.name,
                range,
              });
            }

            // Add table names (with schema prefix)
            for (const table of allTables) {
              suggestions.push({
                label: `${table.schema}.${table.name}`,
                kind: monaco.languages.CompletionItemKind.Class,
                detail: `Table (${table.schema})`,
                insertText: `${table.schema}.${table.name}`,
                range,
              });
              // Also add without schema prefix for convenience
              suggestions.push({
                label: table.name,
                kind: monaco.languages.CompletionItemKind.Class,
                detail: `Table (${table.schema})`,
                insertText: table.name,
                range,
              });
            }

            // Add all columns (with full path suggestion)
            for (const table of allTables) {
              for (const column of table.columns) {
                suggestions.push({
                  label: `${table.schema}.${table.name}.${column.name}`,
                  kind: monaco.languages.CompletionItemKind.Field,
                  detail: `${column.type} (${table.schema}.${table.name})`,
                  insertText: `${table.schema}.${table.name}.${column.name}`,
                  range,
                });
              }
            }
          }

          return { suggestions };
        },
      });

    return () => {
      completionDisposableRef.current?.dispose();
    };
  }, [schemas, monaco]);

  // Apply diff decorations, view zones, and floating accept/reject widgets when hunks change
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !monaco) return;

    // Clean up previous view zones (batched in a single layout pass)
    const prevZoneIds = viewZoneIdsRef.current;
    if (prevZoneIds.length > 0) {
      editor.changeViewZones((accessor) => {
        for (const id of prevZoneIds) {
          accessor.removeZone(id);
        }
      });
      viewZoneIdsRef.current = [];
    }
    decorationsCollectionRef.current?.clear();

    if (hunks.length === 0) return;

    const model = editor.getModel();
    if (!model) return;

    if (!decorationsCollectionRef.current) {
      decorationsCollectionRef.current = editor.createDecorationsCollection([]);
    }

    // Track whether this effect has been cleaned up (for async colorize)
    let disposed = false;

    const isDark = theme === "dark";

    const decorations: Monaco.editor.IModelDeltaDecoration[] = [];
    const newViewZoneIds: string[] = [];

    function escapeHtml(text: string): string {
      return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }

    const colors = isDark ? THEME_COLORS.dark : THEME_COLORS.light;
    const removedBg = colors.redBgSubtle;

    function buildLineHtmlWithRemovedRanges(
      line: string,
      removedRanges: { start: number; end: number }[],
    ): string {
      if (removedRanges.length === 0) return escapeHtml(line);
      const parts: string[] = [];
      let lastEnd = 0;
      for (const r of removedRanges) {
        if (r.start > lastEnd) {
          parts.push(escapeHtml(line.slice(lastEnd, r.start)));
        }
        parts.push(
          '<span class="sql-diff-char-removed">',
          escapeHtml(line.slice(r.start, r.end)),
          "</span>",
        );
        lastEnd = r.end;
      }
      if (lastEnd < line.length) {
        parts.push(escapeHtml(line.slice(lastEnd)));
      }
      return parts.join("");
    }

    // Batch all view zone additions in a single changeViewZones call
    editor.changeViewZones((accessor) => {
      for (const hunk of hunks) {
        // Whole-line + character-level decorations for added content (green) in editor
        for (let i = 0; i < hunk.suggestedLines.length; i++) {
          const origLine = hunk.originalLines[i] ?? "";
          const suggLine = hunk.suggestedLines[i] ?? "";
          const { addedRanges } = computeCharLevelRanges(origLine, suggLine);
          const lineNum = hunk.suggestedStartLine + i;
          // Whole-line highlight
          const endColumn = model.getLineMaxColumn(lineNum);
          decorations.push({
            range: {
              startLineNumber: lineNum,
              startColumn: 1,
              endLineNumber: lineNum,
              endColumn,
            },
            options: {
              isWholeLine: true,
              className: "sql-diff-added-line",
            },
          });
          // Character-level extra highlight on changed chars
          for (const r of addedRanges) {
            decorations.push({
              range: {
                startLineNumber: lineNum,
                startColumn: r.start + 1,
                endLineNumber: lineNum,
                endColumn: r.end + 1,
              },
              options: {
                className: "sql-diff-char-added",
              },
            });
          }
        }

        // View zone for removed content (red) — non-editable, no line numbers
        const hasOriginalContent = hunk.originalLines.length > 0;
        if (hasOriginalContent) {
          const afterLine = hunk.suggestedStartLine - 1;
          const contentHeight = hunk.originalLines.length * LINE_HEIGHT;
          const zoneHeight = contentHeight;

          const domNode = document.createElement("div");
          domNode.className = "sql-diff-removed-zone";
          domNode.style.cssText = `height: ${zoneHeight}px; box-sizing: border-box; display: flex; flex-direction: column;`;

          const content = document.createElement("div");
          content.style.cssText = `background: ${removedBg}; padding: 0 8px 0 0; font-family: Monaco, Menlo, monospace; font-size: ${FONT_SIZE}px; line-height: ${LINE_HEIGHT}px; white-space: pre; box-sizing: border-box; min-width: 0; overflow: hidden; flex: 0 0 ${contentHeight}px;`;
          // Build per-line elements with async syntax highlighting
          hunk.originalLines.forEach((origLine, i) => {
            const suggLine = hunk.suggestedLines[i] ?? "";
            const { removedRanges } = computeCharLevelRanges(
              origLine,
              suggLine,
            );

            const lineDiv = document.createElement("div");
            lineDiv.style.cssText = `position: relative; height: ${LINE_HEIGHT}px; line-height: ${LINE_HEIGHT}px; white-space: pre;`;

            // Initial content without syntax highlighting (shown until colorize resolves)
            lineDiv.innerHTML = buildLineHtmlWithRemovedRanges(
              origLine,
              removedRanges,
            );
            content.appendChild(lineDiv);

            // Asynchronously add syntax highlighting via Monaco's colorizer
            if (origLine.length > 0) {
              monaco.editor
                .colorize(origLine, "sql", {})
                .then((colorizedHtml) => {
                  if (disposed) return;

                  // Strip trailing <br/> that colorize appends
                  const cleanHtml = colorizedHtml.replace(/<br\s*\/?>\s*$/, "");

                  // Two-layer rendering:
                  // Background layer — char-level diff highlights with transparent text
                  // Foreground layer — syntax-highlighted text from colorize
                  let html = "";
                  if (removedRanges.length > 0) {
                    html += `<span style="position:absolute;top:0;left:0;color:transparent;pointer-events:none;white-space:pre;">${buildLineHtmlWithRemovedRanges(origLine, removedRanges)}</span>`;
                  }
                  html += `<span style="position:relative;">${cleanHtml}</span>`;

                  lineDiv.innerHTML = html;
                });
            }
          });

          domNode.appendChild(content);

          const zoneId = accessor.addZone({
            afterLineNumber: Math.max(0, afterLine),
            heightInPx: zoneHeight,
            domNode,
          });
          newViewZoneIds.push(zoneId);
        }
      }
    });

    decorationsCollectionRef.current?.set(decorations);
    viewZoneIdsRef.current = newViewZoneIds;

    return () => {
      disposed = true;
      decorationsCollectionRef.current?.clear();
      editor.changeViewZones((accessor) => {
        for (const id of newViewZoneIds) {
          accessor.removeZone(id);
        }
      });
    };
  }, [hunks, monaco, theme]);

  const handleMount = (
    editor: Monaco.editor.IStandaloneCodeEditor,
    monaco: typeof Monaco,
  ) => {
    editorRef.current = editor;
    decorationsCollectionRef.current = editor.createDecorationsCollection([]);
    setIsEditorReady(true);
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyP, () => {});
    editor.addCommand(
      monaco.KeyMod.Shift | monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyP,
      () => {},
    );
    editor.addCommand(monaco.KeyCode.F1, () => {});
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF, () => {});
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyH, () => {});
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () =>
      onCommandEnterRef.current?.(),
    );

    // Calculate the exact content area position
    const updateContentLeft = () => {
      const layoutInfo = editor.getLayoutInfo();
      // contentLeft includes line numbers width + padding
      setContentLeft(layoutInfo.contentLeft);
    };

    // Initial calculation
    updateContentLeft();

    // Update on layout changes
    editor.onDidLayoutChange(updateContentLeft);

    // Set up focus/blur listeners for placeholder
    editor.onDidFocusEditorWidget(() => {
      setIsFocused(true);
    });

    editor.onDidBlurEditorWidget(() => {
      setIsFocused(false);
    });
  };

  const showPlaceholder =
    placeholder && !editorValue && !isFocused && isEditorReady;

  return (
    <div
      ref={containerRef}
      className="relative flex h-full min-h-0 w-full flex-col"
    >
      <style>
        {(() => {
          const c = theme === "dark" ? THEME_COLORS.dark : THEME_COLORS.light;
          return `
        .monaco-editor .line-numbers { color: ${c.gray} !important; }
        .sql-diff-added-line { background-color: ${c.greenBgSubtle}; }
        .sql-diff-removed-line { background-color: ${c.redBgSubtle}; }
        .sql-diff-removed { background-color: ${c.redBgHover}; }
        .sql-diff-char-added { background-color: ${c.greenBgHover}; }
        .sql-diff-char-removed { background-color: ${c.redBgHover}; }
      `;
        })()}
      </style>
      <div className="min-h-0 min-w-0 flex-1">
        <Editor
          loading={<Skeleton className="m-2 h-[calc(100%-1rem)] w-full" />}
          defaultLanguage="sql"
          value={editorValue}
          onChange={handleChange}
          theme={theme === "dark" ? "index-dark" : "index-light"}
          beforeMount={handleBeforeMount}
          onMount={handleMount}
          options={{
            minimap: { enabled: false },
            fontSize: FONT_SIZE,
            lineNumbers: "on",
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
            wordWrap: "off",
            contextmenu: false,
            renderLineHighlight: "none",
            roundedSelection: false,
            overviewRulerLanes: 0,
            scrollbar: {
              vertical: "auto",
              horizontal: "auto",
              useShadows: false,
              verticalScrollbarSize: 8,
              horizontalScrollbarSize: 8,
            },
            stickyScroll: { enabled: false },
            padding: { bottom: LINE_HEIGHT, top: LINE_HEIGHT },
          }}
        />
      </div>
      {showPlaceholder && contentLeft > 0 && (
        <div
          className="pointer-events-none absolute top-0 left-0 text-muted-foreground"
          style={{
            paddingLeft: `${contentLeft}px`,
            paddingTop: `${LINE_HEIGHT}px`,
            fontSize: "13px",
            fontFamily:
              "Monaco, Menlo, 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace",
            lineHeight: `${LINE_HEIGHT}px`,
            letterSpacing: "normal",
          }}
        >
          {placeholder}
        </div>
      )}
    </div>
  );
};
